import { useMemo, useState } from 'react';
import { BellRing, Plus, Users } from 'lucide-react';
import { NOTIFICATION_CATALOG, SystemRole, routableNotificationsFor } from '@peles/shared';
import { notificationGroupUsage, type NotificationGroup } from '../../api/notificationRouting';
import { useAuth } from '../../context/AuthContext';
import { useDialog } from '../../context/DialogContext';
import {
  useDeleteNotificationGroup,
  useNotificationGroups,
  useNotificationRules,
  useResolvedPeople,
  useRoutingPeople,
} from '../../hooks/useNotificationRouting';
import { CollapsibleSection } from '../common/CollapsibleSection';
import { NotificationCatalogList } from './NotificationCatalogList';
import { NotificationGroupCard } from './NotificationGroupCard';
import { NotificationGroupFormModal } from './NotificationGroupFormModal';
import { NotificationRuleRow } from './NotificationRuleRow';
import {
  byCategory,
  deleteGroupMessage,
  effectiveUses,
  personFor,
  sendToChoices,
} from './routingView';

export interface NotificationRoutingScreenProps {
  /** One event's groups and rules; omit for the office-wide ones. */
  eventId?: string | null;
  eventName?: string;
}

const EMPTY: never[] = [];

/**
 * Who gets each notification — the one screen for distribution groups,
 * office-wide (Settings → Notifications) and for one event (its Notifications
 * tab). The main thing on it is the list of notifications, each with a plain
 * "Sends to" choice; the groups those choices name sit below as cards; and the
 * full inventory of what the app sends is folded away at the bottom.
 */
export function NotificationRoutingScreen({
  eventId = null,
  eventName,
}: NotificationRoutingScreenProps) {
  const isEvent = eventId !== null;
  const { user } = useAuth();
  const { confirm, alert } = useDialog();
  const role = user?.systemRole;
  const isAdmin = role === SystemRole.ADMIN;
  const canEdit = isEvent ? isAdmin || role === SystemRole.PLANNER : isAdmin;

  const groupsQuery = useNotificationGroups(eventId);
  const rulesQuery = useNotificationRules(eventId);
  const peopleQuery = useRoutingPeople(eventId);
  // An event's page also reads the office-wide groups (they may be chosen) and
  // rules (what "Default" falls back to). On the office-wide page these are the
  // same queries as above, so nothing is asked twice.
  const officeGroupsQuery = useNotificationGroups(null);
  const officeRulesQuery = useNotificationRules(null);

  const groups = groupsQuery.data ?? EMPTY;
  const rules = rulesQuery.data ?? EMPTY;
  const people = peopleQuery.data ?? EMPTY;
  const officeGroups = isEvent ? (officeGroupsQuery.data ?? EMPTY) : EMPTY;
  const officeRules = isEvent ? (officeRulesQuery.data ?? EMPTY) : EMPTY;

  const allMemberIds = useMemo(
    () => [...new Set([...groups, ...officeGroups].flatMap((g) => g.memberIds))],
    [groups, officeGroups],
  );
  const unknownIds = allMemberIds.filter((id) => !people.some((p) => p.id === id));
  const resolvedQuery = useResolvedPeople(eventId, unknownIds);
  const resolved = resolvedQuery.data ?? EMPTY;

  const deleteGroup = useDeleteNotificationGroup();
  const [editing, setEditing] = useState<NotificationGroup | 'new' | null>(null);

  const entries = routableNotificationsFor(isEvent ? 'event' : 'global');
  const categories = byCategory(entries);

  // On an event the office-wide groups and rules decide what "Default" means
  // and which groups can be picked, so the page waits for them too, and a
  // failure to read them is a failure — never shown as "nothing routed".
  const loading =
    groupsQuery.isLoading ||
    rulesQuery.isLoading ||
    (isEvent && (officeGroupsQuery.isLoading || officeRulesQuery.isLoading));
  const failed =
    groupsQuery.error ??
    rulesQuery.error ??
    (isEvent ? (officeGroupsQuery.error ?? officeRulesQuery.error) : null);

  const membersOf = (group: NotificationGroup) =>
    group.memberIds.map((id) => personFor(id, people, resolved));

  const removeGroup = async (group: NotificationGroup) => {
    // Every rule naming it, events' included: an office-wide group may be what
    // other events' rules point at. Failing that, what this page knows.
    let message: string;
    try {
      message = deleteGroupMessage({
        usage: await notificationGroupUsage(group.id),
        eventId,
        catalog: NOTIFICATION_CATALOG,
      });
    } catch {
      message = deleteGroupMessage({
        usage: rules
          .filter((rule) => rule.groupId === group.id)
          .map((rule) => ({ event: rule.event, eventId })),
        eventId,
        catalog: NOTIFICATION_CATALOG,
      });
    }
    const ok = await confirm({
      title: `Delete “${group.name}”?`,
      message,
      confirmLabel: 'Delete',
      variant: 'danger',
    });
    if (!ok) return;
    try {
      await deleteGroup.mutateAsync(group.id);
    } catch (err) {
      await alert({
        title: 'The group was not deleted',
        message: err instanceof Error ? err.message : 'Please try again.',
        variant: 'error',
      });
    }
  };

  if (failed) {
    return (
      <p role="alert" className="text-sm text-red-600">
        {failed instanceof Error
          ? failed.message
          : 'The notification settings could not be loaded.'}
      </p>
    );
  }

  return (
    <div className="max-w-5xl space-y-8">
      <section className="rounded-xl border border-primary-100 bg-primary-50/60 p-4 sm:p-5">
        <div className="flex items-start gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white text-primary-600">
            <BellRing size={20} aria-hidden="true" />
          </span>
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-gray-900">
              {isEvent
                ? `Who hears about ${eventName ?? 'this event'}`
                : 'Choose who gets each notification'}
            </h2>
            <p className="mt-1 text-sm text-gray-600">
              {isEvent
                ? 'Choices here apply to this event only. Anything you leave alone follows Settings → Notifications.'
                : 'Each notification goes to everyone in the office until you pick a group for it. An event can choose differently on its own Notifications tab.'}
            </p>
            <ol className="mt-3 flex flex-col gap-2 text-sm text-gray-700 sm:flex-row sm:gap-6">
              <li className="flex items-center gap-2">
                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary-600 text-xs font-semibold text-white">
                  1
                </span>
                Make a group of people
              </li>
              <li className="flex items-center gap-2">
                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary-600 text-xs font-semibold text-white">
                  2
                </span>
                Pick that group for the notifications it should get
              </li>
            </ol>
          </div>
        </div>
      </section>

      <section aria-labelledby="groups-heading">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h3 id="groups-heading" className="text-base font-semibold text-gray-900">
              <span className="text-primary-600">Step 1 ·</span>{' '}
              {isEvent ? 'This event’s groups' : 'Groups'}
            </h3>
            <p className="text-sm text-gray-500">
              {isEvent
                ? 'People in the office, and this event’s clients if you like.'
                : 'Named lists of people in the office.'}
            </p>
          </div>
          {canEdit && (
            <button
              type="button"
              onClick={() => setEditing('new')}
              className="inline-flex min-h-11 items-center gap-1.5 rounded-lg bg-primary-600 px-4 text-sm font-medium text-white hover:bg-primary-700 sm:min-h-9"
            >
              <Plus size={16} aria-hidden="true" />
              New group
            </button>
          )}
        </div>

        {!loading && groups.length === 0 ? (
          <div className="mt-3 flex flex-col items-center gap-2 rounded-xl border border-dashed border-gray-300 px-4 py-8 text-center">
            <Users size={28} className="text-gray-400" aria-hidden="true" />
            <p className="text-sm font-medium text-gray-700">No groups yet</p>
            <p className="max-w-md text-sm text-gray-500">
              {isEvent
                ? 'Make one for the people on this event — for example the producer running it and the couple.'
                : 'Make one for each team that should hear about something — for example a Money team for payments.'}
            </p>
          </div>
        ) : (
          <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {groups.map((group) => (
              <NotificationGroupCard
                key={group.id}
                group={group}
                members={membersOf(group)}
                usedFor={effectiveUses(group.id, entries, rules, officeRules)}
                onEdit={canEdit ? () => setEditing(group) : undefined}
                onDelete={canEdit ? () => void removeGroup(group) : undefined}
              />
            ))}
          </div>
        )}

        {isEvent && officeGroups.length > 0 && (
          <div className="mt-6">
            <h4 className="text-sm font-medium text-gray-800">
              Office-wide groups you can also pick
            </h4>
            <p className="mb-2 text-xs text-gray-500">
              Changed in Settings → Notifications, by an administrator.
            </p>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {officeGroups.map((group) => (
                <NotificationGroupCard
                  key={group.id}
                  group={group}
                  members={membersOf(group)}
                  usedFor={effectiveUses(group.id, entries, rules, officeRules)}
                  badge="Office-wide"
                />
              ))}
            </div>
          </div>
        )}
      </section>

      <section aria-labelledby="who-gets-heading">
        <h3 id="who-gets-heading" className="text-base font-semibold text-gray-900">
          <span className="text-primary-600">Step 2 ·</span> Who gets each notification
        </h3>
        <p className="mt-1 text-sm text-gray-500">
          {canEdit
            ? 'Pick a group under “Sends to”. Changes save as you make them.'
            : 'Only an administrator can change these.'}
        </p>
        {loading ? (
          <div className="mt-3 space-y-2" aria-busy="true">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-14 animate-pulse rounded-xl bg-gray-100" />
            ))}
          </div>
        ) : (
          categories.map(({ category, entries: list }) => (
            <div key={category} className="mt-4">
              <h4 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500">
                {category}
              </h4>
              <ul className="divide-y divide-gray-100 rounded-xl border border-gray-200 bg-white">
                {list.map((entry) => (
                  <NotificationRuleRow
                    key={entry.key}
                    entry={entry}
                    eventId={eventId}
                    rule={rules.find((r) => r.event === entry.key) ?? null}
                    choices={sendToChoices({
                      entry,
                      isEvent,
                      groups,
                      officeGroups,
                      officeRule: officeRules.find((r) => r.event === entry.key) ?? null,
                    })}
                    inheritedChannels={officeRules.find((r) => r.event === entry.key)?.channels}
                    canEdit={canEdit}
                  />
                ))}
              </ul>
            </div>
          ))
        )}
      </section>

      {!isEvent && (
        <CollapsibleSection
          id="notification-catalog"
          storageKey="settings:notification-catalog"
          title="Everything the app sends"
          defaultOpen={false}
        >
          <p className="mb-3 text-sm text-gray-500">
            For reference: every email and notification, what sets it off and who gets it. Those
            marked “Can route to a group” appear in the list above; the wording of each is edited in
            Settings → Messaging.
          </p>
          <NotificationCatalogList />
        </CollapsibleSection>
      )}

      <NotificationGroupFormModal
        open={editing !== null}
        onClose={() => setEditing(null)}
        eventId={eventId}
        group={editing === 'new' ? null : editing}
        people={people}
        extraPeople={resolved}
      />
    </div>
  );
}
